import React, { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
  getUserInfo,
  setAccessToken,
  loginAudit,
  selectLogin
} from '../../components/SecureRoute/slice'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import {
  PageWrapper,
  HeroSection,
  HeroContent,
  HeroText,
  HeroTitle,
  HeroSubtitle,
  HeroDescription,
  ButtonGroup,
  LoginBtn,
  FeaturesSection,
  FeatureGrid,
  FeatureCard,
  FeatureIcon,
  FeatureTitle,
  FeatureText,
  FooterSection
} from './HomePage.styled'

const HomePage: React.FC = () => {
  const navigate = useNavigate()
  const { isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently } =
    useAuth0()
  const dispatch = useAppDispatch()
  const loginResponse = useAppSelector(selectLogin)
  const [open, setOpen] = useState(false)

  // eslint-disable-next-line
  const handleLogin = async () => {
    // eslint-disable-next-line
    loginWithRedirect()
  }

  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated) {
        const status = loginResponse?.status
        if (status === 'ACTIVE') {
          setOpen(false)
          navigate('/dashboard')
        } else if (status === 'NEW') {
          setOpen(false)
          navigate('/request-access')
        } else if (status === 'PENDING') {
          setOpen(false)
          navigate('/pending-access')
        } else {
          setOpen(false)
          navigate('/unauthorized')
        }
      }
    }
  }, [loginResponse])

  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated) {
        setOpen(true)
        // eslint-disable-next-line
        getAccessTokenSilently().then((token) => {
          dispatch(setAccessToken(token))
          // eslint-disable-next-line
          dispatch(getUserInfo())
          // eslint-disable-next-line
          dispatch(loginAudit())
        })
      }
    }
  }, [isLoading, isAuthenticated])

  return (
    <PageWrapper>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={open}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <HeroSection>
        <HeroContent>
          <HeroText>
            <HeroTitle>Management Portal</HeroTitle>
            <HeroSubtitle>
              Manage restaurants, menus, and dining experiences with ease
            </HeroSubtitle>
            <HeroDescription>
              A comprehensive platform designed for restaurant partners and administrators
              to streamline operations, engage customers, and grow their business.
            </HeroDescription>
          </HeroText>

          <ButtonGroup>
            <LoginBtn onClick={() => { void handleLogin() }}>Continue</LoginBtn>
          </ButtonGroup>
        </HeroContent>
      </HeroSection>

      <FeaturesSection>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h2 style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#0a1e3d',
            margin: '0 0 12px 0',
            fontFamily: "'Montserrat', sans-serif"
          }}>
            Powerful Features for Your Success
          </h2>
          <p style={{
            fontSize: '16px',
            color: '#5a6b7a',
            margin: '0',
            fontFamily: "'Montserrat', sans-serif"
          }}>
            Everything you need to manage your restaurant operations
          </p>
        </div>

        <FeatureGrid>
          <FeatureCard>
            <FeatureIcon>📊</FeatureIcon>
            <FeatureTitle>Dashboard Analytics</FeatureTitle>
            <FeatureText>
              Real-time insights and analytics to track your restaurant performance
            </FeatureText>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>🍽️</FeatureIcon>
            <FeatureTitle>Menu Management</FeatureTitle>
            <FeatureText>
              Create, update, and organize your menus with intuitive tools
            </FeatureText>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>📍</FeatureIcon>
            <FeatureTitle>Location Management</FeatureTitle>
            <FeatureText>
              Manage multiple locations and keep information always up to date
            </FeatureText>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>⭐</FeatureIcon>
            <FeatureTitle>Reviews & Ratings</FeatureTitle>
            <FeatureText>
              Monitor and respond to customer reviews and ratings
            </FeatureText>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>👥</FeatureIcon>
            <FeatureTitle>User Management</FeatureTitle>
            <FeatureText>
              Control access and manage admin and eater accounts
            </FeatureText>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>📱</FeatureIcon>
            <FeatureTitle>Mobile Optimized</FeatureTitle>
            <FeatureText>
              Manage your restaurant on the go with mobile-friendly interface
            </FeatureText>
          </FeatureCard>
        </FeatureGrid>
      </FeaturesSection>

      <FooterSection>
        <p>© 2024 Resactly. All rights reserved. | Professional Restaurant Management Platform</p>
      </FooterSection>
    </PageWrapper>
  )
}

export default HomePage
